// FAQ content as data (rich-content blocks). One source feeds both the /faq UI
// (via RichText) and the FAQPage JSON-LD (via blocksToPlainText).

import { ACTION_COSTS, FREE_MONTHLY_CREDITS, PACK_EXPIRY_MONTHS } from "../constants/credits";

/** @typedef {{ id: string, question: string, answer: import('./richText').RichBlock[] }} FaqEntry */

const n = (/** @type {number} */ v) => v.toLocaleString("en-US");

/** @type {FaqEntry[]} */
export const faqData = [
  {
    id: "what-is-listing-analysis",
    question: "What is a Listino listing package?",
    answer: [
      {
        type: "p",
        spans: [
          "Listino helps prepare properties for market by creating personalized seller materials backed by property research, market insights, and pricing guidance.",
        ],
      },
      {
        type: "p",
        spans: [
          "If your property is already listed on Zillow, Listino can automatically import the listing details, photos, and description. If you're preparing a property for market, simply provide the property details and any photos you'd like to include.",
        ],
      },
      {
        type: "p",
        spans: [
          "Every listing package includes property research, market analysis, pricing guidance, comparable sales, recommendations, and seller-ready materials that can be personalized before sharing with clients.",
        ],
      },
      {
        type: "p",
        spans: [
          "Everything is fully editable, making it easy to update pricing, replace photos, refine recommendations, apply your branding, and tailor the final package to your client.",
        ],
      },
    ],
  },
  {
    id: "how-long-does-it-take",
    question: "How long does it take to receive my listing package?",
    answer: [
      {
        type: "p",
        spans: [
          "Your listing prep runs in the background. Timing varies with the listing, selected extras, and current demand. We'll email you when it's ready; you can follow its progress and open available results from Your listings.",
        ],
      },
    ],
  },
  {
    id: "who-can-use-this",
    question: "Do I need to be the listing agent?",
    answer: [
      {
        type: "p",
        spans: [
          "Listino is designed for listing agents, but anyone preparing a property for market can use it. Whether you're preparing for a listing consultation, launching a new listing, refreshing a listing that's been on the market, or selling your own home, Listino helps organize the research, strategy, and seller materials before they're shared.",
        ],
      },
    ],
  },
  {
    id: "no-zillow-listing",
    question: "What if I don't have a Zillow listing?",
    answer: [
      {
        type: "p",
        spans: [
          "No problem! You can ",
          { text: "enter your property details manually", href: "/submit" },
          " and upload your own photos. This is perfect if you're preparing to list, selling on another platform, or just want feedback before going live.",
        ],
      },
    ],
  },
  {
    id: "whats-included",
    question: "What's included in my listing package?",
    answer: [
      { type: "p", spans: ["Research:"] },
      {
        type: "ul",
        items: [
          "Property details and market analysis",
          "Comparable sales and a Quick CMA",
          "Buyer insights and neighborhood context",
          "Market positioning and pricing analysis",
        ],
      },
      { type: "p", spans: ["Strategy:"] },
      {
        type: "ul",
        items: [
          "Pricing guidance",
          "Property specific recommendations",
          "Staging recommendations",
          "Seller talking points and positioning",
        ],
      },
      { type: "p", spans: ["Presentation:"] },
      {
        type: "ul",
        items: [
          "Personalized seller materials",
          "Listing description",
          "AI photo enhancements",
          "PDF, PowerPoint, one-pager, and social content",
        ],
      },
    ],
  },
  {
    id: "pricing",
    question: "How is Listino priced?",
    answer: [
      {
        type: "p",
        spans: [
          `Every account includes ${n(FREE_MONTHLY_CREDITS)} credits a month, free, with no card required. Credits pay for what Listino prepares: a Listing Analysis is ${n(ACTION_COSTS.analysis)} credits, and files such as a branded PDF, Seller Presentation, one-pager, or social set are small additions on top. Editing, branding, comps curation, sharing, and publishing never use credits.`,
        ],
      },
      {
        type: "p",
        spans: [
          "If you prepare more than the free month covers, the Agent and Pro plans include more credits each month or year, and credit packs are available any time. Everything Listino prepares is available on every plan. Visit our ",
          { text: "pricing page", href: "/pricing" },
          " for the current prices.",
        ],
      },
    ],
  },
  {
    id: "what-is-a-credit",
    question: "What is a credit?",
    answer: [
      {
        type: "p",
        spans: [
          `A credit is the unit Listino uses to price the work it prepares. Prices are shown once, where you make the choice: a Listing Analysis is ${n(ACTION_COSTS.analysis)} credits, a branded PDF ${n(ACTION_COSTS.branded_pdf)}, a photo touch-up ${n(ACTION_COSTS.photo_touchup)} per photo. Credits only leave your balance when you start something, and if anything fails to prepare they come back automatically.`,
        ],
      },
      {
        type: "p",
        spans: [
          `Credits on a monthly plan (including the Free plan) reset each billing month and don't roll over. Annual plans are one pool for the year. Credit packs are good for ${PACK_EXPIRY_MONTHS} months, or through the end of your current term if you're on a plan.`,
        ],
      },
    ],
  },
  {
    id: "watermark",
    question: "Is there a watermark on the Free plan?",
    answer: [
      {
        type: "p",
        spans: [
          "Files exported on the Free plan carry a small Listino mark. Upgrading to Agent or Pro removes it from everything you've already prepared, not just new listings.",
        ],
      },
    ],
  },
  {
    id: "guarantee",
    question: "Can you guarantee a listing will sell faster?",
    answer: [
      {
        type: "p",
        spans: [
          "While we can't guarantee specific results, every recommendation is based on real estate best practices and current market data. We focus on specific, practical guidance you can act on right away — what to highlight, what to improve, and how to present the listing in the best possible light.",
        ],
      },
    ],
  },
  {
    id: "when-charged",
    question: "When will my card be charged?",
    answer: [
      {
        type: "p",
        spans: [
          "Only when you buy something: a plan or a credit pack. Using your monthly free credits never involves a card. Plans renew automatically at the start of each term and packs are a one-time charge. We use Stripe for secure payment processing.",
        ],
      },
    ],
  },
  {
    id: "refund-policy",
    question: "What if I'm not satisfied with my listing prep?",
    answer: [
      {
        type: "p",
        spans: [
          "We stand behind our work. Contact us at help@listino.ai with specific feedback and we'll work to address it. Any plan or credit-pack purchase can be refunded in full within 7 days of the purchase; a refund removes only the unspent credits from that purchase. If anything Listino prepares fails, the credits come back automatically.",
        ],
      },
    ],
  },
  {
    id: "other-platforms",
    question: "Do you work with listings on other platforms besides Zillow?",
    answer: [
      {
        type: "p",
        spans: [
          "Yes! While we can automatically import from Zillow, you can also ",
          { text: "submit your listing manually", href: "/submit" },
          " for any platform — Realtor.com, Redfin, FSBO, or even pre-listing preparation. Just enter your property details and upload your photos.",
        ],
      },
    ],
  },
  {
    id: "implement-changes",
    question: "How do I implement the recommended changes?",
    answer: [
      {
        type: "p",
        spans: [
          "Each recommendation in your prep includes impact, cost, and effort guidance so you know exactly what to prioritize. Most recommendations are easy to act on yourself or to hand to your photographer, stager, or handyman.",
        ],
      },
    ],
  },
  {
    id: "sharing-and-revoking",
    question: "How do I share it, and can I take it back?",
    answer: [
      {
        type: "p",
        spans: [
          "Publish your listing package and Listino creates a web link you can send to a seller, plus PDF, PowerPoint, one-pager, and social versions to download. The link always shows the version you published, never a draft you are still working on.",
        ],
      },
      {
        type: "p",
        spans: [
          "You stay in control of it: revoke the link at any time from the Share step and it stops working immediately, for everyone you sent it to.",
        ],
      },
    ],
  },
];
